import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  TextField,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useVerifyResetOTPMutation, useResendResetOTPMutation } from '../../store/api/authApi';
import { palette } from '../../theme/palette';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';

const VerifyResetOTPPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { language } = useAppSelector((state) => state.ui);
  const [verifyOTP, { isLoading }] = useVerifyResetOTPMutation();
  const [resendOTP, { isLoading: isResending }] = useResendResetOTPMutation();

  const email = (location.state as { email?: string })?.email || '';
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last digit
    setOtp(newOtp);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const nextEmpty = newOtp.findIndex((v) => !v);
      inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError(t('auth.verifyOTP.otpRequired'));
      return;
    }

    try {
      const result = await verifyOTP({ email, otp: otpCode }).unwrap();
      const token = result.data?.token;
      setSuccess(t('auth.verifyOTP.verified'));
      setTimeout(() => {
        navigate('/reset-password', { state: { token } });
      }, 1000);
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      setError(error?.data?.message || t('auth.verifyOTP.error'));
    }
  };

  const handleResend = async () => {
    setError(null);
    setSuccess(null);
    try {
      await resendOTP({ email }).unwrap();
      setSuccess(t('auth.verifyOTP.resent'));
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch {
      setError(t('auth.verifyOTP.resendError'));
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Card
        elevation={8}
        sx={{
          width: '100%',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('auth.verifyOTP.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('auth.verifyOTP.subtitle')}
            </Typography>
            {email && (
              <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, color: palette.brandSecondary[500] }}>
                {email}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* OTP Input Boxes */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 1, sm: 1.5 },
                mb: 3,
                direction: 'ltr', // OTP always LTR
              }}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => { inputRefs.current[index] = el; }}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      padding: '12px 0',
                    },
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: { xs: 42, sm: 52 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.brand[500],
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              ))}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || otp.join('').length !== 6}
              sx={{
                py: 1.5,
                mb: 2,
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                '&:hover': {
                  background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
                },
              }}
            >
              {isLoading ? t('auth.verifyOTP.verifying') : t('auth.verifyOTP.verify')}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary" component="span">
                {t('auth.verifyOTP.didntReceive')}{' '}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: palette.brandSecondary[500],
                  cursor: isResending ? 'default' : 'pointer',
                  fontWeight: 600,
                  '&:hover': { textDecoration: isResending ? 'none' : 'underline' },
                }}
                onClick={!isResending ? handleResend : undefined}
              >
                {isResending ? t('auth.verifyOTP.resending') : t('auth.verifyOTP.resend')}
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="text"
              startIcon={<ArrowBackIcon sx={{ transform: language === 'ar' ? 'rotate(180deg)' : 'none' }} />}
              onClick={() => navigate('/forgot-password')}
              sx={{ color: palette.brandSecondary[500] }}
            >
              {t('auth.verifyOTP.back')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyResetOTPPage;

